import { ComponentProps, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import locale from "@bonliva-crm/locale";
import { MultiSelect, sortSelectedFirst } from "@bonliva-crm/ui";
import { Workshift } from "@bonliva-crm/shared";
import { useUpdateEffect } from "@hooks";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";

export type PreferredScheduleFilterData = Workshift[];

type Option = { label: string; value: string | null };

export type PreferredScheduleFilterProps = FilterWithFavouriteProps;

export const PreferredScheduleFilter = (props: PreferredScheduleFilterProps) => {
  const options = useMemo(() => {
    return Object.values(Workshift).toLabelValue((w) => locale.workshifts[w]);
  }, []);

  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale.PreferredSchedule;
  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const commonProps: ComponentProps<typeof MultiSelect<Option>> = {
    options,
    value: field?.value
      ? Object.values(field?.value).toLabelValue((w) => locale.workshifts[w as Workshift])
      : [],
    onChange: (selected) => {
      setValue(
        props.name,
        selected.map((s) => s.value as Workshift),
        { shouldDirty: true }
      );
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 300,
    showBadge: field?.value !== null,
    optionType: "checkbox",
    isClearable: field?.value !== null,
    onClear: () => setValue(props.name, null, { shouldDirty: true }),
    sortOptionsOnMenuOpenFn: (options) =>
      sortSelectedFirst({
        options,
        value: field?.value,
        valueOptionEqualityFn: (value, option) => value === option.value
      }),
    closeMenuOnSelect: false,
    hidePlaceholderOnFocus: false,
    showOptionDivider: false
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
